import { useState } from "react";
import useSWR from "swr";
import { Plus, Trash2, CheckCircle2, Loader2, Link2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { Field } from "@/pages/admin/ContentPanels";

const fetcher = (url) => adminApi.get(url);

export default function PaymentsPanel() {
  const { data: items, mutate } = useSWR("/admin/payments", fetcher);
  const [form, setForm] = useState({ client_name: "", client_email: "", description: "", amount: "", currency: "usd" });
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.post("/admin/payments", { ...form, amount: parseFloat(form.amount) });
      toast.success("Payment request created — share the private link with your buyer");
      setForm({ client_name: "", client_email: "", description: "", amount: "", currency: "usd" });
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}/pay/${id}`;
    if (navigator.clipboard) navigator.clipboard.writeText(url);
    toast.success("Payment link copied — send it to your buyer: " + url);
  };

  const setStatus = async (id, status) => {
    await adminApi.put(`/admin/payments/${id}/status`, { status });
    toast.success(status === "paid" ? "Marked as paid — receipt email sent to the buyer" : "Marked as pending");
    mutate();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this payment request?")) return;
    await adminApi.del(`/admin/payments/${id}`);
    toast.success("Deleted");
    mutate();
  };

  return (
    <div className="max-w-4xl" data-testid="admin-payments-panel">
      <form onSubmit={create} className="card p-6" data-testid="payment-create-form">
        <h2 className="font-serif text-lg font-semibold text-navy">New Payment Request</h2>
        <p className="mt-1 text-xs leading-relaxed text-mist">
          Create an invoice-style request and share the private payment link with your buyer. The buyer opens the link to pay
          (card gateway or your bank details), or you mark it paid after a bank transfer — a receipt email is sent automatically.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Client Name">
            <input className="field" required value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} data-testid="payment-client-name" />
          </Field>
          <Field label="Client Email (receipt)">
            <input className="field" type="email" value={form.client_email} onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))} data-testid="payment-client-email" />
          </Field>
          <Field label="Description (what is this payment for?)" full>
            <input className="field" required value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="e.g. Advance payment — Basmati Rice order, PI #001" data-testid="payment-description" />
          </Field>
          <Field label="Amount">
            <input className="field" type="number" min="1" step="0.01" required value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="payment-amount" />
          </Field>
          <Field label="Currency">
            <select className="field" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} data-testid="payment-currency">
              <option value="usd">USD — US Dollar</option>
              <option value="eur">EUR — Euro</option>
              <option value="gbp">GBP — British Pound</option>
            </select>
          </Field>
        </div>
        <button type="submit" disabled={busy} className="btn-gold mt-5" data-testid="payment-create-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create Payment Request
        </button>
      </form>

      <div className="mt-6 space-y-3">
        {(items || []).length === 0 && (
          <div className="card p-6 text-sm text-mist" data-testid="payments-empty-state">No payment requests yet — create the first one above.</div>
        )}
        {(items || []).map((p) => (
          <div key={p.id} className="card flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`payment-row-${p.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`chip !text-[10px] uppercase ${p.status === "paid" ? "!border-green-300 !bg-green-50 !text-green-700" : ""}`} data-testid={`payment-status-${p.id}`}>{p.status}</span>
                <h3 className="font-bold text-navy">{p.client_name}</h3>
                <span className="font-serif text-sm font-semibold text-navy">
                  {Number(p.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {String(p.currency).toUpperCase()}
                </span>
              </div>
              <p className="mt-1 text-xs text-mist">{p.description}{p.client_email ? ` · ${p.client_email}` : ""}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => copyLink(p.id)} className="btn-navy !px-3 !py-2 !text-[11px]" data-testid={`payment-copy-link-${p.id}`}>
                <Link2 size={13} /> Copy Link
              </button>
              {p.status !== "paid" ? (
                <button onClick={() => setStatus(p.id, "paid")} className="btn-gold !px-3 !py-2 !text-[11px]" data-testid={`payment-mark-paid-${p.id}`}>
                  <CheckCircle2 size={13} /> Mark Paid
                </button>
              ) : (
                <button onClick={() => setStatus(p.id, "pending")} className="btn-outline !px-3 !py-2 !text-[11px]" data-testid={`payment-undo-${p.id}`}>
                  <RotateCcw size={13} /> Undo
                </button>
              )}
              <button onClick={() => remove(p.id)} className="rounded-lg border border-line p-2 text-red-600 hover:border-red-300" aria-label="Delete payment request" data-testid={`payment-delete-${p.id}`}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
